/** Lo que necesitamos de cada trozo de `page.getTextContent()` de pdf.js. */
export interface PdfTextItem {
  str?: string;
  hasEOL?: boolean;
  /** Matriz de pdf.js: `transform[5]` es la altura de la línea en la página. */
  transform?: number[];
  height?: number;
}

/**
 * Convierte el texto de una página de PDF en párrafos para volver a partirlos
 * con letra grande. Un PDF no tiene párrafos, solo trozos colocados: se junta
 * lo que va seguido, se empieza párrafo donde hay más aire entre líneas del
 * habitual y se recompone la palabra que un guion partió a final de línea.
 */
export function reflowPdfText(items: readonly PdfTextItem[]): string[] {
  const lines: { text: string; y: number | null; height: number }[] = [];
  let text = "";
  let y: number | null = null;
  let height = 0;
  const cerrar = (): void => {
    if (text.trim() !== "") lines.push({ text: text.trim().replace(/\s+/g, " "), y, height });
    text = "";
    y = null;
    height = 0;
  };
  for (const item of items) {
    if (typeof item.str !== "string") continue;
    const itemY = item.transform?.[5] ?? null;
    // Otra altura sin salto marcado: pdf.js no siempre avisa del final de línea
    if (text.trim() !== "" && itemY != null && y != null && Math.abs(itemY - y) > Math.max(2, height * 0.5)) cerrar();
    if (item.str !== "" && y == null) y = itemY;
    text += item.str;
    height = Math.max(height, item.height ?? 0);
    if (item.hasEOL === true) cerrar();
  }
  cerrar();

  const paragraphs: string[] = [];
  let current = "";
  lines.forEach((line, i) => {
    const previous = lines[i - 1];
    const gap = previous?.y != null && line.y != null ? previous.y - line.y : null;
    const grande = Math.max(previous?.height ?? 0, line.height);
    // Otro tamaño de letra también separa: un título no es parte del párrafo
    const otraLetra = previous != null && Math.abs(previous.height - line.height) > grande * 0.15;
    const nuevo = otraLetra || (gap != null && (gap < 0 || gap > grande * 1.6));
    if (nuevo && current !== "") {
      paragraphs.push(current);
      current = "";
    }
    if (current === "") current = line.text;
    else if (/[\p{L}]-$/u.test(current) && /^\p{Ll}/u.test(line.text)) current = current.slice(0, -1) + line.text;
    else current += ` ${line.text}`;
  });
  if (current !== "") paragraphs.push(current);
  return paragraphs;
}
